import { useEffect, useState } from "react";
import { Plus, Building2, Tags, PieChart, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function CategoryManager({ endpoint, label, testid }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const load = () => api.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const add = async () => {
    if (!name.trim()) return;
    await api.post(endpoint, { name: name.trim() });
    setName(""); load(); toast.success("Category added");
  };
  const toggle = async (c) => { await api.put(`${endpoint}/${c.id}`, { is_active: !c.is_active }); load(); };
  const startEdit = (c) => { setEditId(c.id); setEditName(c.name); };
  const saveEdit = async (c) => {
    if (!editName.trim()) return;
    await api.put(`${endpoint}/${c.id}`, { name: editName.trim() });
    setEditId(null); load(); toast.success("Category updated");
  };
  const confirmDelete = async () => {
    await api.delete(`${endpoint}/${toDelete.id}`);
    setToDelete(null); load(); toast.success("Category deleted");
  };

  return (
    <div data-testid={testid}>
      <div className="mb-4 flex gap-2">
        <Input placeholder={`New ${label} name`} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid={`${testid}-add-input`} />
        <Button onClick={add} className="gap-2 bg-orange-600 hover:bg-orange-700" data-testid={`${testid}-add-btn`}><Plus className="h-4 w-4" /> Add</Button>
      </div>
      <div className="divide-y divide-slate-100">
        {items.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-2 py-3" data-testid={`cat-row-${c.id}`}>
            {editId === c.id ? (
              <div className="flex flex-1 items-center gap-2">
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(c)} autoFocus data-testid={`cat-edit-input-${c.id}`} />
                <button onClick={() => saveEdit(c)} className="rounded-lg p-2 text-emerald-600 hover:bg-emerald-50" data-testid={`cat-save-${c.id}`}><Check className="h-4 w-4" /></button>
                <button onClick={() => setEditId(null)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <span className={c.is_active ? "text-slate-700" : "text-slate-400 line-through"}>{c.name}</span>
            )}
            {editId !== c.id && (
              <div className="flex items-center gap-1">
                <Switch checked={c.is_active} onCheckedChange={() => toggle(c)} />
                <button onClick={() => startEdit(c)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-blue-600" data-testid={`cat-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setToDelete(c)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-600" data-testid={`cat-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete category?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{toDelete?.name}"? Existing transactions keep their recorded category, but it won't be available for new entries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="cat-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Settings() {
  const { refresh } = useApp();
  const [biz, setBiz] = useState({ business_name: "SocietyBites", currency: "INR", financial_year: "April - March" });

  useEffect(() => { api.get("/settings").then((r) => setBiz(r.data)); }, []);

  const saveBiz = async () => {
    await api.put("/settings", biz);
    toast.success("Business settings saved");
    refresh();
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Settings</h1>
        <p className="mt-1 text-slate-500">Manage business info and categories</p>
      </div>

      <Tabs defaultValue="business">
        <TabsList>
          <TabsTrigger value="business" className="gap-2"><Building2 className="h-4 w-4" /> Business</TabsTrigger>
          <TabsTrigger value="revenue" className="gap-2"><Tags className="h-4 w-4" /> Revenue Types</TabsTrigger>
          <TabsTrigger value="expense" className="gap-2"><Tags className="h-4 w-4" /> Expense Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="business">
          <div className="max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 font-heading font-bold text-slate-900">Business Information</h3>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Business Name</Label><Input value={biz.business_name} onChange={(e) => setBiz({ ...biz, business_name: e.target.value })} data-testid="biz-name" /></div>
              <div className="space-y-2"><Label>Currency</Label><Input value={biz.currency} onChange={(e) => setBiz({ ...biz, currency: e.target.value })} /></div>
              <div className="space-y-2"><Label>Financial Year</Label><Input value={biz.financial_year} onChange={(e) => setBiz({ ...biz, financial_year: e.target.value })} /></div>
              <Button onClick={saveBiz} className="bg-orange-600 hover:bg-orange-700" data-testid="save-business">Save Changes</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="revenue">
          <div className="max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 font-heading font-bold text-slate-900">Revenue Types</h3>
            <CategoryManager endpoint="/revenue-categories" label="revenue type" testid="revenue-categories-manager" />
          </div>
        </TabsContent>

        <TabsContent value="expense">
          <div className="max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 font-heading font-bold text-slate-900">Expense Categories</h3>
            <CategoryManager endpoint="/expense-categories" label="expense category" testid="expense-categories-manager" />
          </div>
        </TabsContent>
      </Tabs>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2 text-slate-700"><PieChart className="h-5 w-5 text-orange-500" /><h3 className="font-heading font-bold">Profit Allocation</h3></div>
        <p className="mt-1 text-sm text-slate-500">Manage allocation percentages on the dedicated Profit Allocation page.</p>
        <Button asChild variant="outline" className="mt-3"><a href="/allocation">Go to Profit Allocation</a></Button>
      </div>
    </div>
  );
}
